import { ConnectionHandler, type RecordSourceSelectorProxy, type RecordProxy } from 'relay-runtime';
import RelayDataID from '@atlassian/relay-data-id';
import {
	GRAPHQL_FIELD_AGS_RELATIONSHIP_PROJECTS,
	GRAPHQL_FIELD_GRAPH_STORE,
	GRAPHQL_TYPE_JIRA_AVATAR,
	GRAPHQL_TYPE_JIRA_PROJECT,
} from '../../../common/constant';
import type { ConnectedProjectProxy } from '../../../common/types';

/**
 * This fixture takes place only when NO CONNECTED PROJECTS ARE FETCHED DURING INITIAL PAGE LOAD.
 * E.g.: When the user sets the connected project for a very first time.
 * In this case, after setting a first connected project Relay ignores manually updated client-side store during re-fetching.
 * This fires an unnecessary network call to BE to fetch the connected projects. Due to eventual consistency, BE still returns
 * empty list of connected projects. As a result, the user isn't able to see the releases on the calendar view
 * immediately after choosing a connected project.
 *
 * The root cause of invalidated cache during re-fetching is missing 'client:__type:JiraProject'
 * record in the client-side store which Relay is trying to find while traversing the query.
 * This record is created automatically by Relay when at least one connected project is fetched from BE.
 *
 * To reproduce this issue, pause the debugger inside "_proto._handleMissing" of the following file:
 * relay-runtime/lib/store/DataChecker.js
 * and investigate the variables across the call stack.
 *
 * relay-runtime/lib/query/fetchQuery.js environment.check(operation).status === 'available'
 * relay-runtime/lib/store/RelayModernEnvironment.js _proto.check
 * relay-runtime/lib/store/RelayModernEnvironment.js _proto._checkSelectorAndHandleMissingFields
 * relay-runtime/lib/store/RelayModernStore.js _proto.check
 * relay-runtime/lib/store/DataChecker.js _proto.check
 * relay-runtime/lib/mutations/RelayRecordSourceMutator.js _proto.getValue
 * */
const addMissingClientTypeIfNecessary = (store: RecordSourceSelectorProxy) => {
	const clientRecordId = `client:__type:${GRAPHQL_TYPE_JIRA_PROJECT}`;
	let clientRecord = store.get(clientRecordId);

	if (!clientRecord) {
		clientRecord = store.create(clientRecordId, GRAPHQL_TYPE_JIRA_PROJECT);
		clientRecord.setValue(clientRecordId, '__id');
		clientRecord.setValue(true, '__isNode');
		clientRecord.setValue('__TypeSchema', '__typename');
	}
};

export const updateStore = (
	store: RecordSourceSelectorProxy,
	connectedProjectProxies: ConnectedProjectProxy[],
	currentProjectAri: string,
) => {
	const graphStoreRecord: RecordProxy = store.getRoot().getLinkedRecord(GRAPHQL_FIELD_GRAPH_STORE);

	if (!graphStoreRecord) {
		return;
	}

	const connection: RecordProxy = graphStoreRecord.getLinkedRecord(
		GRAPHQL_FIELD_AGS_RELATIONSHIP_PROJECTS,
		{
			id: currentProjectAri,
		},
	);

	if (!connection) {
		return;
	}

	connectedProjectProxies.forEach((project) => {
		const { id, name, avatarUrl, resourceId } = project;

		const projectNodeId = RelayDataID({ id: resourceId }, GRAPHQL_TYPE_JIRA_PROJECT);

		if (!projectNodeId) {
			return;
		}

		// check if project node is already in store so that Relay
		// if project already exists in the store, we will still call createEdge to added it to an existing Edge
		// (createEdge) method will check if Edge already exists or not (if exists, it will re-use the existing one)
		// then it will add/replace new node in the edge
		let projectNode = store.get(projectNodeId);

		if (!projectNode) {
			addMissingClientTypeIfNecessary(store);
			projectNode = store.create(projectNodeId, GRAPHQL_TYPE_JIRA_PROJECT);
			projectNode.setValue(projectNodeId, 'id');
			projectNode.setValue(name, 'name');
			projectNode.setValue(id, 'projectId');
		}

		if (avatarUrl) {
			const avatarNodeId = RelayDataID({ id: resourceId }, GRAPHQL_TYPE_JIRA_AVATAR);
			// check if avatar node is already in store so that Relay does not throw an error if it already exists
			if (avatarNodeId && !store.get(avatarNodeId)) {
				const avatarNode = store.create(avatarNodeId, GRAPHQL_TYPE_JIRA_AVATAR);
				avatarNode.setValue(avatarNodeId, 'id');
				avatarNode.setValue(avatarUrl, 'small');
				projectNode.setLinkedRecord(avatarNode, 'avatar');
			}
		}

		const newEdge = ConnectionHandler.createEdge(
			store,
			connection,
			projectNode,
			'GraphStoreSimplifiedProjectHasRelatedWorkWithProjectEdge',
		);
		ConnectionHandler.insertEdgeAfter(connection, newEdge);
	});
};
